import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, CloudUpload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { RefreshTools, ToolsState } from "./ToolsLayout";
import { invalidateTools } from "./toolsData";

export default function CategoriesList() {
  const categoriesQuery = useApiEp("user/product-categories"),
    subsQuery = useApiEp("user/get-product-subcategories");
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(""),
    [dialog, setDialog] = useState(params.get("new") === "1" ? { type: "category" } : null),
    [name, setName] = useState(""),
    [image, setImage] = useState(null),
    [imagePreview, setImagePreview] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : [],
    subs = Array.isArray(subsQuery.data) ? subsQuery.data : [];
  const malformed =
    (categoriesQuery.data && !Array.isArray(categoriesQuery.data)) ||
    (subsQuery.data && !Array.isArray(subsQuery.data));
  const refresh = () => Promise.all([categoriesQuery.refetch(), subsQuery.refetch()]);
  const open = (next) => {
    setDialog(next);
    setName(next.edit?.category || "");
    setImage(null);
    setImagePreview("");
    setError("");
  };
  const close = () => {
    setDialog(null);
    if (params.has("new")) {
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  };
  const choose = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG or WebP image up to 5 MB.");
      return;
    }
    setImage(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy || !dialog) return;
    const value = name.trim();
    if (dialog.type !== "delete" && !value) {
      setError("Enter a name before saving.");
      return;
    }
    const duplicate =
      dialog.type === "category"
        ? categories.some(
            (row) =>
              row._id !== dialog.edit?._id && row.category.toLowerCase() === value.toLowerCase(),
          )
        : dialog.type === "subcategory" &&
          subs.some(
            (row) =>
              row.categoryId === dialog.parent._id &&
              row.subCategory.toLowerCase() === value.toLowerCase(),
          );
    if (duplicate) {
      setError("This name already exists. Choose another name.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (dialog.type === "delete")
        await axios.delete(
          `${API_ENDPOINT}user/${dialog.category ? "delete-product-category" : "delete-subcategory"}/${encodeURIComponent(dialog.record._id)}`,
          getHeader(),
        );
      else if (dialog.type === "subcategory")
        await axios.post(
          `${API_ENDPOINT}user/add-subcategory-product`,
          { categoryId: dialog.parent._id, subCategory: value },
          getHeader(),
        );
      else {
        const body = new FormData();
        body.append("category", value);
        if (image) body.append("image", image);
        const headers = { ...getHeader().headers };
        Object.keys(headers).forEach((key) => {
          if (key.toLowerCase() === "content-type") delete headers[key];
        });
        await axios({
          method: dialog.edit ? "put" : "post",
          url: `${API_ENDPOINT}user/${dialog.edit ? "update-product-category/" + encodeURIComponent(dialog.edit._id) : "add-product-category"}`,
          data: body,
          headers,
        });
      }
      await invalidateTools(client);
      await subsQuery.refetch();
      setNotice(dialog.type === "delete" ? "Category record deleted." : "Category record saved.");
      close();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save this change. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const filtered = categories.filter((row) =>
    (row.category || "").toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <ToolsLayout
      title="Product categories"
      description="Organise the catalogue so customers can find the right tools."
      actions={
        <>
          <RefreshTools
            onClick={refresh}
            loading={categoriesQuery.isFetching || subsQuery.isFetching}
          />
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => open({ type: "category" })}
          >
            Add category
          </Button>
        </>
      }
    >
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <section className="tools-panel">
        <div className="tools-panel-heading">
          <div>
            <Typography component="h2" variant="h6">
              Catalogue categories
            </Typography>
            <p>
              {categoriesQuery.isLoading
                ? "Loading categories…"
                : `${categories.length} categories`}
            </p>
          </div>
          <TextField
            size="small"
            label="Search categories"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <ToolsState
          loading={categoriesQuery.isLoading || subsQuery.isLoading}
          error={categoriesQuery.isError || subsQuery.isError || malformed}
          retry={refresh}
          empty={!filtered.length}
          emptyTitle={search ? "No matching categories" : "Create your first category"}
          emptyMessage={
            search
              ? "Try another name or clear the search."
              : "Add a category before creating products."
          }
          action={
            search ? (
              <Button onClick={() => setSearch("")}>Clear search</Button>
            ) : (
              <Button onClick={() => open({ type: "category" })}>Add category</Button>
            )
          }
        >
          {filtered.map((category) => (
            <Box
              key={category._id}
              sx={{ px: 3, py: 2.5, borderTop: "1px solid", borderColor: "divider" }}
            >
              <div className="tools-panel-heading" style={{ padding: 0, marginBottom: 16 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={category.image} alt="" variant="rounded" />
                  <Typography fontWeight={800}>{category.category}</Typography>
                </Stack>
                <div className="tools-actions">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => open({ type: "subcategory", parent: category })}
                  >
                    Add subcategory
                  </Button>
                  <Button size="small" onClick={() => open({ type: "category", edit: category })}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => open({ type: "delete", category: true, record: category })}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div className="tools-actions">
                {subs
                  .filter((sub) => sub.categoryId === category._id)
                  .map((sub) => (
                    <Chip
                      key={sub._id}
                      label={sub.subCategory}
                      variant="outlined"
                      onDelete={() => open({ type: "delete", category: false, record: sub })}
                    />
                  ))}
                {!subs.some((sub) => sub.categoryId === category._id) && (
                  <Typography variant="caption" color="text.secondary">
                    No subcategories yet.
                  </Typography>
                )}
              </div>
            </Box>
          ))}
        </ToolsState>
      </section>
      <Dialog open={Boolean(dialog)} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>
            {dialog?.type === "delete"
              ? "Delete category record?"
              : dialog?.type === "subcategory"
                ? `Add subcategory · ${dialog.parent.category}`
                : dialog?.edit
                  ? "Edit category"
                  : "Add category"}
          </DialogTitle>
          <DialogContent>
            {dialog?.type === "delete" ? (
              <Typography>
                Delete “{dialog.record.category || dialog.record.subCategory}”?{" "}
                {dialog.category && "Its subcategories will also be removed."}
              </Typography>
            ) : (
              <>
                <TextField
                  autoFocus
                  fullWidth
                  required
                  label={dialog?.type === "subcategory" ? "Subcategory name" : "Category name"}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={busy}
                  sx={{ mt: 1 }}
                />
                {dialog?.type === "category" && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      disabled={busy}
                    >
                      {image ? "Change image" : "Upload category image"}
                      <input
                        hidden
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={choose}
                      />
                    </Button>
                    {image && (
                      <Button
                        disabled={busy}
                        onClick={() => {
                          setImage(null);
                          setImagePreview("");
                        }}
                      >
                        Discard new image
                      </Button>
                    )}
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ my: 1 }}
                      color="text.secondary"
                    >
                      Optional square image · JPG, PNG or WebP · up to 5 MB
                    </Typography>
                    {(imagePreview || dialog.edit?.image) && (
                      <img
                        src={imagePreview || dialog.edit.image}
                        alt="Category preview"
                        style={{ width: 160, height: 160, objectFit: "contain", borderRadius: 12 }}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <LoadingButton
              loading={busy}
              type="submit"
              variant="contained"
              color={dialog?.type === "delete" ? "error" : "primary"}
            >
              {dialog?.type === "delete" ? "Delete" : "Save category"}
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>
    </ToolsLayout>
  );
}
